import { useState, useImperativeHandle, useRef } from "react";
import { useDispatch } from "react-redux";
import { useReactFlow } from "reactflow";
import { styled } from "@mui/material/styles";
import classNames from "classnames";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import SoftAvatar from "components/SoftAvatar";
import { setDraggedNode } from "redux/actions/flowDesigner/flowsActionCreator";
import commonComponents from "layouts/Flow Designer/schemas/commonComponents";
import { THEME_COLORS } from "constants/theme";
import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import { startDragComponent, stopDragComponent } from "redux/actions/flowDesigner/flowsActionCreator";
import defaultConnectorIcon from "assets/images/connector.png";

const Node = styled(Box)(({ theme }) => ({
   width: "100%",
   height: "100%",
   display: "flex",
   alignItems: "center",
   justifyContent: "center",

   "& .component-key": {
      fontSize: "12px !important",
      color: theme.palette.text.secondary
   }
}));

const styles = {
   componentMenuIcon: (theme) => ({
      color: THEME_COLORS[theme.palette.mode.toUpperCase()]?.PRIMARY_FONT
   }),
   nodeLabel: (theme) => ({
      fontSize: "14px",
      wordBreak: "break-word",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "center",
      textTransform: "none",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap"
   })
};

const ToggleDrawer = ({ whatNode, isCommon, isLabel, logo, ref }) => {
   const dispatch = useDispatch();
   const [isDragEnter, setIsDragEnter] = useState(false);
   const { getNode } = useReactFlow();
   const labelNodeTextRef = useRef(null);
   const dragIconRef = useRef(null);

   useImperativeHandle(ref, () => ({
      onNodeDragStart(event) {
         onDragStart(event);
      },
      onNodeDragEnd(event) {
         onDragEnd(event);
      },
      onNodeDragOver(event) {
         onDragOver(event);
      },
      onNodeDragLeave(event) {
         onDragLeave(event);
      },
      onNodeDrop(event) {
         onDrop(event);
      }
   }));

   const generateComponentIcon = () => {
      const flipIconVertical = whatNode.type === COMMON_COMPONENT_TYPES.SELECTION ? "fa-flip-vertical" : "";
      let icon = null;

      if (isCommon) {
         const component = commonComponents.find((c) => c.type === whatNode.type);

         if (component) {
            if (component.type === COMMON_COMPONENT_TYPES.GOTO) {
               icon = <component.icon sx={(theme) => styles.componentMenuIcon(theme)} className={flipIconVertical} />;
            } else {
               icon = <component.icon sx={(theme) => styles.componentMenuIcon(theme)} className={flipIconVertical} />;
            }
         }
      } else {
         icon = <img className={`custom-node-image-${whatNode.id}`} src={logo ? logo : defaultConnectorIcon} alt={whatNode?.label} />;
      }

      if (isCommon) {
         return <SoftAvatar size="lg">{icon}</SoftAvatar>;
      }

      return icon;
   };

   const onDragOver = (event) => {
      event.preventDefault();
      setIsDragEnter(true);
   };

   const onDragLeave = (event) => {
      event.preventDefault();
      setIsDragEnter(false);
   };

   const onDrop = (event) => {
      event.preventDefault();
      setIsDragEnter(false);
   };

   const btnClass = classNames({
      "custom-node-button": true,
      "active-drop-area": isDragEnter,
      "common-node-button": isCommon
   });

   const onDragStart = (event) => {
      event.stopPropagation();
      const nodeData = {
         nodeType: isCommon ? "common" : "custom",
         nodeData: whatNode,
         isClone: whatNode?.clone || false,
         draggedNode: true
      };

      const dragElement = document.querySelector(`.custom-node-wrapper.${whatNode.id}`);
      if (dragElement) {
         const dragImageDiv = document.createElement("div");
         dragImageDiv.style.position = "absolute";
         dragImageDiv.style.top = "-9999px";
         dragImageDiv.style.left = "-9999px";
         dragImageDiv.style.pointerEvents = "none";
         dragImageDiv.style.background = "transparent";
         dragImageDiv.style.boxSizing = "border-box";
         dragImageDiv.style.width = `${dragElement.offsetWidth}px`;
         dragImageDiv.style.height = `${dragElement.offsetHeight}px`;

         // Clone the content
         dragImageDiv.appendChild(dragElement.cloneNode(true));

         document.body.appendChild(dragImageDiv);

         // Use the center of the drag image
         event.dataTransfer.setDragImage(dragImageDiv, dragImageDiv.offsetWidth / 2, dragImageDiv.offsetHeight / 2);

         setTimeout(() => {
            document.body.removeChild(dragImageDiv);
         }, 0);
      }

      event.dataTransfer.setData("application/reactflow", JSON.stringify(nodeData));
      event.dataTransfer.effectAllowed = "move";

      dispatch(setDraggedNode(nodeData));
      dispatch(startDragComponent());
   };

   const onDragEnd = (event) => {
      event.preventDefault();
      dispatch(setDraggedNode(null));
      dispatch(stopDragComponent());
   };

   return (
      <Node id={`btn-node-${whatNode.id}`} className={btnClass} color="success">
         {isLabel ? (
            <Typography ref={labelNodeTextRef} variant="caption" sx={(theme) => styles.nodeLabel(theme)}>
               {getNode(whatNode?.id)?.data?.label}
            </Typography>
         ) : (
            <>
               <div ref={dragIconRef} className={`custom-node-icon ${whatNode.id}`}>
                  {generateComponentIcon()}
               </div>
            </>
         )}
      </Node>
   );
};
export default ToggleDrawer;
